import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Link,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  Avatar,
  ListItemAvatar
} from '@mui/material';
import {
  School as SchoolIcon,
  Code as CodeIcon,
  Assessment as AssessmentIcon,
  Book as BookIcon,
  Work as WorkIcon,
  Language as LanguageIcon,
  Verified as VerifiedIcon,
  AttachMoney as MoneyIcon,
  Public as PublicIcon,
  Star,
  StarBorder,
} from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpIcon from '@mui/icons-material/Help';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import UndoIcon from '@mui/icons-material/Undo';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { ref, get, set, remove } from 'firebase/database';
import { database, auth } from '../../firebase';
import { calculateNextSortOrder } from '../../utils/favoritesUtils';
import { Student } from '../../types/Student';
import { School } from '../../types/schools/School';

import { usePrograms } from '../../contexts/ProgramsContext';
import { useUser } from '../../contexts/UserContext';
import { usePrerequisites } from '../../contexts/PrerequisiteContext';
import { useDebug } from '../../contexts/DebugContext';
import { useSchools } from '../../contexts/SchoolsContext';
import { useGuidanceStudents } from '../../contexts/GuidanceStudentsContext';
import { StudentListItem } from '../students/StudentList';
import { activityLogService } from '../../services/activityLogService';
import CustomEligibilityIndicatorsModal from '../CustomEligibilityIndicatorsModal';


const ProgramProfile: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { 
    programs, 
    loading: programsLoading, 
    error: programsError,
    getProgramEligibility,
    eligibilityLoading,
  } = usePrograms();
  const { user } = useUser();
  const { getPrerequisitesForProgram, prerequisites } = usePrerequisites();
  const { showPrerequisitesDebug } = useDebug();
  const { schools, loading: schoolsLoading } = useSchools();
  const { students: guidanceStudents, loading: guidanceStudentsLoading } = useGuidanceStudents();

  // Helper function to get the correct spelling based on user's country
  const getFavouritesLabel = () => {
    return user?.country === 'US' ? 'Favorites' : 'Favourites';
  };
  const [school, setSchool] = useState<School | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [localEligibilityLoading, setLocalEligibilityLoading] = useState(false);
  
  // Custom Eligibility Indicators modal state
  const [eligibilityIndicatorsModalOpen, setEligibilityIndicatorsModalOpen] = useState(false);

  const category = searchParams.get('category');

  // Get user's grade for Grade 10-specific icons
  const getUserGrade = () => {
    if (!user?.courses) return null;
    
    let derivedGrade = '10'; // Default to Grade 10
    const hasGrade12Courses = Object.values(user.courses).some((course: any) => 
      course.grade === '12' || course.grades === 12
    );
    const hasGrade11Courses = Object.values(user.courses).some((course: any) => 
      course.grade === '11' || course.grades === 11
    );
    
    if (hasGrade12Courses) {
      derivedGrade = '12';
    } else if (hasGrade11Courses) {
      derivedGrade = '11';
    }
    // If no Grade 11 or Grade 12 courses found, keep as Grade 10
    
    return derivedGrade;
  };

  // Helper function to get eligibility status for a program (consistent with other pages)
  const getEligibilityStatus = (program: any) => {
    const eligibility = getCachedEligibility();
    
    
    if (!eligibility) {
      return null;
    }
    
    // If ifEligible is explicitly false, return false
    if (eligibility.ifEligible === false) {
      return false;
    }
    
    // If ifEligible is explicitly true, return true
    if (eligibility.ifEligible === true) {
      return true;
    }
    
    // If ifEligible is null, it means there are some issues.
    // We need to determine if these issues make it "Not Yet Eligible" or "Other".
    if (eligibility.ifEligible === null) {
      const hasMissingCourses = (eligibility.missingCourses?.length || 0) > 0;
      const hasGradeIssues = (eligibility.gradeIssues?.length || 0) > 0;
      
      const userGrade = getUserGrade();
      
      const hasLowAverage = userGrade === '12' &&
                           eligibility.calculatedAverage !== undefined &&
                           eligibility.programAverage !== undefined &&
                           eligibility.calculatedAverage < eligibility.programAverage;

      // If there are any specific reasons for not being eligible (missing courses, grade issues, or low average for G12),
      // then it should be classified as "Not Yet Eligible" (false).
      if (hasMissingCourses || hasGradeIssues || hasLowAverage) {
        return false;
      }
    }
    
    // Default to null for other cases
    return null;
  };

  // Get program from context
  const program = useMemo(() => {
    const foundProgram = programs.find(p => p.id === id) || null;
    return foundProgram;
  }, [programs, id]);

  // Determine the next 4 graduating classes based on current month (for guidance users)
  const graduatingClasses = useMemo(() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11, so add 1 for 1-12

    if (currentMonth < 7) {
      // Jan-Jun: current year, current year +1, current year +2, current year +3
      return [
        currentYear.toString(),
        (currentYear + 1).toString(),
        (currentYear + 2).toString(),
        (currentYear + 3).toString(),
      ];
    } else {
      // Jul-Dec: current year +1, current year +2, current year +3, current year +4
      return [
        (currentYear + 1).toString(),
        (currentYear + 2).toString(),
        (currentYear + 3).toString(),
        (currentYear + 4).toString(),
      ];
    }
  }, []);

  // Get students who favorited this program at this school (for guidance users only)
  const studentsWhoFavoritedProgram = useMemo(() => {
    // Only show for guidance users
    if (!user?.isGuidanceUser || !program) {
      return [];
    }

    // Create the formatted program string to match against
    const formattedProgram = `${program.program} - ${program.schoolName}`;

    // Filter students who have this program favorited AND are in the next 4 graduating classes
    const filteredStudents = guidanceStudents.filter((student) => {
      // Check if student is in the next 4 graduating classes
      const studentClassYear = student.grade; // grade is the Class Year
      if (!graduatingClasses.includes(studentClassYear)) {
        return false;
      }

      // Check if student has this program favorited
      if (!student.favoriteProgramsWithSchools || student.favoriteProgramsWithSchools.length === 0) {
        return false;
      }
      return student.favoriteProgramsWithSchools.includes(formattedProgram);
    });

    // Sort by last name, then first name (alpha sort)
    const sortedStudents = [...filteredStudents].sort((a, b) => {
      const lastNameCompare = (a.lastName || '').localeCompare(b.lastName || '');
      if (lastNameCompare !== 0) {
        return lastNameCompare;
      }
      return (a.firstName || '').localeCompare(b.firstName || '');
    });

    return sortedStudents;
  }, [user?.isGuidanceUser, program, guidanceStudents, graduatingClasses]);

  // Helper function to get cached eligibility data from ProgramsContext
  const getCachedEligibility = () => {
    if (!program) return null;
    return getProgramEligibility(program.id);
  };

  // Log program pageview when program is loaded (only once per program ID)
  const [loggedProgramId, setLoggedProgramId] = useState<string | null>(null);
  useEffect(() => {
    if (program && id && user?.uid && loggedProgramId !== id) {
      try {
        activityLogService.logProgramPageview(user.uid, id);
        setLoggedProgramId(id);
      } catch (error) {
        console.error('Error logging program pageview activity:', error);
        // Don't block the flow if logging fails
      }
    }
  }, [program, id, user?.uid, loggedProgramId]);

  // Check if current user has this program as favorite
  const checkIfFavorite = async () => {
    try {
      const currentUser = auth.currentUser;
      
      // Clear favorite status if no user or no program
      if (!currentUser || !program) {
        setIsFavorite(false);
        return;
      }


      const favoriteRef = ref(database, `favoriteSchoolProgram/${program.schoolName}/${program.id}/${currentUser.uid}`);
      const snapshot = await get(favoriteRef);
      const isFav = snapshot.exists();
      setIsFavorite(isFav);
    } catch (error) {
      console.error('Error checking favorite status:', error);
      setIsFavorite(false);
    }
  };

  // Toggle favorite status
  const toggleFavorite = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !program) return;

      setFavoriteLoading(true);
      const favoriteRef = ref(database, `favoriteSchoolProgram/${program.schoolName}/${program.id}/${currentUser.uid}`);
      const userFavoritesRef = ref(database, `user/${currentUser.uid}/favorites/${program.id}`);

      if (isFavorite) {
        // Remove from favorites
        await Promise.all([
          remove(favoriteRef),
          remove(userFavoritesRef)
        ]);
        setIsFavorite(false);
        
        // Log the program removed from favorites activity
        try {
          await activityLogService.logProgramRemovedFromFavorites(currentUser.uid, program.id);
        } catch (error) {
          console.error('Error logging program removed from favorites activity:', error);
          // Don't block the flow if logging fails
        }
      } else {
        // Add to favorites
        const timestamp = new Date().toISOString();
        const sortOrder = await calculateNextSortOrder(currentUser.uid);
        
        await Promise.all([
          set(favoriteRef, true),
          set(userFavoritesRef, {
            FavoritedImageURL: '',
            FavoritedLocation: `${program.schoolName}, ${program.province || ''}`,
            FavoritedName: program.program || program.id,
            FavoritedSortOrder: sortOrder,
            FavoritedTimestamp: timestamp,
            FavoritedType: 'program',
            FavoritedUID: currentUser.uid,
            ProgramID: program.id,
            SchoolID: ''
          })
        ]);
        setIsFavorite(true);
        
        // Log the program added to favorites activity
        try {
          await activityLogService.logProgramAddedToFavorites(currentUser.uid, program.id);
        } catch (error) {
          console.error('Error logging program added to favorites activity:', error);
          // Don't block the flow if logging fails
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Find the school object from the Canadian schools context when the program is loaded
  useEffect(() => {
    if (!program || !program.schoolName || schoolsLoading) {
      return;
    }
    
    // Find the school from the Canadian schools context data
    const foundSchool = schools.find(school => school.name === program.schoolName);
    
    if (foundSchool) {
      setSchool(foundSchool);
    } else {
      setSchool(null);
    }
  }, [program, schools, schoolsLoading]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Check favorite status when program loads and auth state is stable
  useEffect(() => {
    if (program && !programsLoading) {
      checkIfFavorite();
    }
  }, [program, programsLoading, user]);

  // Handle program website click
  const handleProgramWebsiteClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (program?.url && user?.uid) {
      // Log the external program website referral activity
      try {
        await activityLogService.logExternalProgramWebsiteReferral(user.uid, program.id);
      } catch (error) {
        console.error('Error logging external program website referral activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Format the URL properly
      let formattedUrl = program.url.trim();
      if (!/^https?:\/\//i.test(formattedUrl)) {
        formattedUrl = 'https://' + formattedUrl;
      }
      
      // Try different approaches for mobile vs desktop
      try {
        if (isMobile) {
          // For mobile, try using location.href first, then fallback to window.open
          window.location.href = formattedUrl;
        } else {
          // For desktop, use window.open
          window.open(formattedUrl, '_blank');
        }
      } catch (error) {
        console.error('Error opening external link:', error);
        // Fallback: try window.open for all devices
        window.open(formattedUrl, '_blank');
      }
    }
  };

  // Note: Eligibility data is now managed by ProgramsContext cache
  // No need to trigger calculations here - the cache will provide the data

  // Reset local loading state when program changes
  useEffect(() => {
    setLocalEligibilityLoading(false);
  }, [id]);


  // Get courses and grades from different grade levels in hierarchical order
  const displayCourses = useMemo(() => {
    if (!user?.courses) return { courses: [], gradeLevel: null };
    
    // First try to find Grade 12 courses
    const grade12Courses = Object.entries(user.courses)
      .filter(([courseCode, courseData]) => {
        return courseData.grade === '12' || courseData.grades === 12;
      })
      .map(([courseCode, courseData]) => ({
        courseCode,
        grade: courseData.grades || 0,
        gradeLevel: '12'
      }))
      .sort((a, b) => b.grade - a.grade); // Sort by grade descending
    
    if (grade12Courses.length > 0) {
      return { courses: grade12Courses, gradeLevel: '12' };
    }
    
    // If no Grade 12 courses, try Grade 11
    const grade11Courses = Object.entries(user.courses)
      .filter(([courseCode, courseData]) => {
        return courseData.grade === '11' || courseData.grades === 11;
      })
      .map(([courseCode, courseData]) => ({
        courseCode,
        grade: courseData.grades || 0,
        gradeLevel: '11'
      }))
      .sort((a, b) => b.grade - a.grade); // Sort by grade descending
    
    if (grade11Courses.length > 0) {
      return { courses: grade11Courses, gradeLevel: '11' };
    }
    
    // If no Grade 11 courses, try Grade 10
    const grade10Courses = Object.entries(user.courses)
      .filter(([courseCode, courseData]) => {
        return courseData.grade === '10' || courseData.grades === 10;
      })
      .map(([courseCode, courseData]) => ({
        courseCode,
        grade: courseData.grades || 0,
        gradeLevel: '10'
      }))
      .sort((a, b) => b.grade - a.grade); // Sort by grade descending
    
    if (grade10Courses.length > 0) {
      return { courses: grade10Courses, gradeLevel: '10' };
    }
    
    // If no courses found at any level
    return { courses: [], gradeLevel: null };
  }, [user?.courses]);

  // Get prerequisite courses for this program
  const prerequisiteCourses = useMemo(() => {
    if (!program) return { exactMatches: new Set<string>(), patterns: { has4M: false, has4U: false, requiredCount: 0 } };
    
    const prerequisiteData = getPrerequisitesForProgram(program.id);
    if (!prerequisiteData?.PrerequisiteList) return { exactMatches: new Set<string>(), patterns: { has4M: false, has4U: false, requiredCount: 0 } };
    

    
    const prerequisiteCourseCodes = new Set<string>();
    const hasPatternRequirements = { has4M: false, has4U: false, requiredCount: 0 };
    
    // Extract all course codes from the prerequisite list
    for (const [, group] of Object.entries(prerequisiteData.PrerequisiteList)) {
      const minCountRequired = parseInt(group.MinCountRequired || "1");
      hasPatternRequirements.requiredCount = Math.max(hasPatternRequirements.requiredCount, minCountRequired);
      
      for (const courseCode of Object.keys(group.CourseList)) {
        // Handle pattern-based requirements (like "4M", "4U")
        if (courseCode === "4M") {
          hasPatternRequirements.has4M = true;
        } else if (courseCode === "4U") {
          hasPatternRequirements.has4U = true;
        } else {
          prerequisiteCourseCodes.add(courseCode);
        }
      }
    }
    
    return { exactMatches: prerequisiteCourseCodes, patterns: hasPatternRequirements };
  }, [program, getPrerequisitesForProgram]);

  // Get the top N prerequisite courses for pattern-based requirements
  const topPrerequisiteCourses = useMemo(() => {
    if (!prerequisiteCourses.patterns.has4M && !prerequisiteCourses.patterns.has4U) {
      return new Set<string>();
    }
    
    if (!user?.courses) return new Set<string>();
    
    // Find all courses that match the pattern requirements
    const matchingCourses = Object.entries(user.courses)
      .filter(([courseCode, courseData]) => {
        // Filter for the current display grade level (or Grade 12 if no specific level)
        const currentGradeLevel = displayCourses.gradeLevel || '12';
        if (courseData.grade !== currentGradeLevel && courseData.grades !== parseInt(currentGradeLevel)) return false;
        
        // Check if course matches the pattern requirements
        const matches4M = prerequisiteCourses.patterns.has4M && courseCode.includes('4M');
        const matches4U = prerequisiteCourses.patterns.has4U && courseCode.includes('4U');
        
        return matches4M || matches4U;
      })
      .map(([courseCode, courseData]) => ({
        courseCode,
        grade: courseData.grades || 0
      }))
      .sort((a, b) => b.grade - a.grade); // Sort by grade descending
    
    // Take the top N courses based on required count
    const requiredCount = prerequisiteCourses.patterns.requiredCount;
    const topCourses = matchingCourses.slice(0, requiredCount);
    
    return new Set(topCourses.map(course => course.courseCode));
  }, [prerequisiteCourses, user?.courses, displayCourses.gradeLevel]);

  if (programsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (programsError || !program) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{programsError || 'Program not found.'}</Alert>
      </Box>
    );
  }

  // Helper function to get border color based on eligibility status
  const getEligibilityBorderColor = () => {
    const eligibility = getCachedEligibility();
    
    // Only show grey if we have no eligibility data at all
    if (!eligibility) {
      return 'grey.400'; // Grey for no eligibility data
    }
    
    const userGrade = getUserGrade();
    const isGrade10 = userGrade === '10';
    
    if (eligibility.ifEligible === true) {
      if (isGrade10) {
        return '#8dd439'; // Fosterway green for Grade 10 eligible
      } else {
        return '#2e7d32'; // Success green for other grades eligible
      }
    } else if (eligibility.ifEligible === false) {
      if (isGrade10) {
        // Grade 10: Check if it's a grade issue vs missing courses
        const hasGradeIssues = eligibility.gradeIssues && eligibility.gradeIssues.length > 0;
        const hasMissingCourses = eligibility.missingCourses && eligibility.missingCourses.length > 0;
        
        if (hasGradeIssues) {
          return '#ffc107'; // Yellow for grade issues
        } else if (hasMissingCourses) {
          return '#1976d2'; // Blue for missing courses
        } else {
          return '#1976d2'; // Blue fallback
        }
      } else {
        // Other grades: Check if this is a Grade 12 student with low average
        if (userGrade === '12' && 
            eligibility.calculatedAverage && 
            eligibility.programAverage && 
            eligibility.calculatedAverage < eligibility.programAverage &&
            (!eligibility.missingCourses || eligibility.missingCourses.length === 0) &&
            (!eligibility.gradeIssues || eligibility.gradeIssues.length === 0)) {
          return '#1976d2'; // Blue for low average
        } else {
          return '#1976d2'; // Blue for other issues
        }
      }
    } else {
      // isEligible is null - check if there are missing courses or grade issues
      const hasGradeIssues = eligibility.gradeIssues && eligibility.gradeIssues.length > 0;
      const hasMissingCourses = eligibility.missingCourses && eligibility.missingCourses.length > 0;
      
      // Check if this is a Grade 12 student with low average (even when isEligible is null)
      if (userGrade === '12' && 
          eligibility.calculatedAverage && 
          eligibility.programAverage && 
          eligibility.calculatedAverage < eligibility.programAverage &&
          (!hasMissingCourses) &&
          (!hasGradeIssues)) {
        return '#1976d2'; // Blue for low average
      } else if (hasGradeIssues || hasMissingCourses) {
        return '#1976d2'; // Blue for missing courses or grade issues
      } else {
        return '#8dd439'; // Fosterway green for no issues found
      }
    }
  };

  // Helper function to render eligibility icon in smaller size
  const renderEligibilityIcon = (size = 24) => {
    const eligibility = getCachedEligibility();
    
    if (!eligibility || localEligibilityLoading) {
      return (
        <Box sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: 'grey.400',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: (localEligibilityLoading || eligibilityLoading) ? 'pulse 1.5s ease-in-out infinite' : 'none'
        }}>
          <HelpIcon sx={{ color: 'white', fontSize: size * 0.7 }} />
        </Box>
      );
    }

    const userGrade = getUserGrade();
    const isGrade10 = userGrade === '10';
    
    if (eligibility.ifEligible === true) {
      if (isGrade10) {
        // Grade 10: Fosterway green circle with white full vertical arrow for on track
        return (
          <Box sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            backgroundColor: '#8dd439', // Fosterway green (lighter shade)
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <ArrowUpwardIcon sx={{ color: 'white', fontSize: size * 0.7 }} />
          </Box>
        );
      } else {
        // Other grades: Original green check
        return <CheckCircleIcon sx={{ color: 'success.main', fontSize: size }} />;
      }
    } else if (eligibility.ifEligible === false) {
      if (isGrade10) {
        // Grade 10: Check if it's a grade issue vs missing courses
        const hasGradeIssues = eligibility.gradeIssues && eligibility.gradeIssues.length > 0;
        const hasMissingCourses = eligibility.missingCourses && eligibility.missingCourses.length > 0;
        
        if (hasGradeIssues) {
          // Grade 10: Yellow circle with white full vertical arrow for grades need work
          return (
            <Box sx={{
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: '#ffc107', // Yellow
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ArrowUpwardIcon sx={{ color: 'white', fontSize: size * 0.7 }} />
            </Box>
          );
        } else if (hasMissingCourses) {
          // Grade 10: Blue circle with white undo arrow for missing prerequisites
          return (
            <Box sx={{
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <UndoIcon sx={{ color: 'white', fontSize: size * 0.7 }} />
            </Box>
          );
        } else {
          // Grade 10: Fallback
          return (
            <Box sx={{
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <UndoIcon sx={{ color: 'white', fontSize: size * 0.7 }} />
            </Box>
          );
        }
      } else {
        // Other grades: Check if this is a Grade 12 student with low average
        if (userGrade === '12' && 
            eligibility.calculatedAverage && 
            eligibility.programAverage && 
            eligibility.calculatedAverage < eligibility.programAverage &&
            (!eligibility.missingCourses || eligibility.missingCourses.length === 0) &&
            (!eligibility.gradeIssues || eligibility.gradeIssues.length === 0)) {
          // Grade 12: Blue circle with split arrow for low average
          return (
            <Box sx={{
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <AltRouteIcon sx={{ color: 'white', fontSize: size * 0.7 }} />
            </Box>
          );
        } else {
          // Other grades: Original blue circle with split arrow
          return (
            <Box sx={{
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <AltRouteIcon sx={{ color: 'white', fontSize: size * 0.7 }} />
            </Box>
          );
        }
      }
    } else {
      // isEligible is null - check if there are missing courses or grade issues
      const hasGradeIssues = eligibility.gradeIssues && eligibility.gradeIssues.length > 0;
      const hasMissingCourses = eligibility.missingCourses && eligibility.missingCourses.length > 0;
      
      // Check if this is a Grade 12 student with low average (even when isEligible is null)
      if (userGrade === '12' && 
          eligibility.calculatedAverage && 
          eligibility.programAverage && 
          eligibility.calculatedAverage < eligibility.programAverage &&
          (!hasMissingCourses) &&
          (!hasGradeIssues)) {
        // Grade 12: Blue circle with split arrow for low average
        return (
          <Box sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            backgroundColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <AltRouteIcon sx={{ color: 'white', fontSize: size * 0.7 }} />
          </Box>
        );
      } else if (hasGradeIssues || hasMissingCourses) {
        // Show blue split arrow for missing courses or grade issues
        return (
          <Box sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            backgroundColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <AltRouteIcon sx={{ color: 'white', fontSize: size * 0.7 }} />
          </Box>
        );
      } else {
        // No issues found - show green checklist
        return (
          <Box sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            backgroundColor: '#8dd439',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <AssignmentIcon sx={{ color: 'white', fontSize: size * 0.7 }} />
          </Box>
        );
      }
    }
  };

  const handleSchoolClick = async () => {
    try {
      // Find the school from the Canadian schools context data
      const foundSchool = schools.find(school => school.name === program.schoolName);
      
      if (foundSchool) {
        // Pass the program ID as a URL parameter so the back button knows where to return
        navigate(`/schools/${foundSchool.id}?fromProgram=${program.id}&${searchParams.toString()}`);
      } else {
        console.error('School not found for program:', program.schoolName);
      }
    } catch (err) {
      console.error('Error navigating to school:', err);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', lg: 'row' } }}>
      {/* Main Content */}
      <Box sx={{ flex: 2 }}>
        <Button variant="outlined" sx={{ mb: 3 }} onClick={() => {
          const fromSchool = searchParams.get('fromSchool');
          const fromEdvisor = searchParams.get('fromEdvisor');
          const fromStudent = searchParams.get('fromStudent');
          const fromDashboard = location.state?.from === 'dashboard';
          const returningFromAdvancedSearch = sessionStorage.getItem('returningFromAdvancedSearch');
          
          const fromGuidance = searchParams.get('fromGuidance');
          
          console.log('🔄 ProgramProfile: Navigating back with params:', {
            fromGuidance,
            fromSchool,
            fromEdvisor,
            fromStudent,
            fromDashboard,
            returningFromAdvancedSearch,
            currentParams: searchParams.toString(),
            category: searchParams.get('category'),
            programSearch: searchParams.get('programSearch'),
            schoolSearch: searchParams.get('schoolSearch'),
            province: searchParams.get('province')
          });
          
          if (fromGuidance === '1') {
            // If we came from guidance dashboard, navigate back to guidance dashboard
            navigate('/guidance-dashboard');
          } else if (fromStudent) {
            // If we came from a student profile (guidance user viewing student), navigate back to student profile
            navigate(`/students/${fromStudent}`);
          } else if (fromEdvisor === '1') {
            console.log('🔄 ProgramProfile: Navigating back to EdVisor');
            // If we came from EdVisor, navigate back to EdVisor
            navigate('/edvisor');
          } else if (returningFromAdvancedSearch === 'true') {
            console.log('🔄 ProgramProfile: Navigating back to Advanced Search');
            // If we came from advanced search, navigate back to advanced search
            // Don't clear the flag here - let Advanced Search handle it after detecting it
            navigate('/advanced-search');
          } else if (fromDashboard) {
            // If we came from the dashboard, navigate back to dashboard
            navigate('/');
          } else if (fromSchool) {
            // If we came from a school profile, navigate back to that school
            navigate(`/schools/${fromSchool}?${searchParams.toString()}`);
          } else {
            // Otherwise, navigate back to programs list with preserved filters
            const currentParams = new URLSearchParams(searchParams);
            // Remove the fromSchool parameter if it exists
            currentParams.delete('fromSchool');
            navigate(`/programs?${currentParams.toString()}`);
          }
        }}>
          &larr; Back
        </Button>


        {/* Program Header */}
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: 2, 
            width: '100%',
            gap: { xs: 2, sm: 0 }
          }}>
            {school && school.SchoolResultsImageURL ? (
              <img
                src={school.SchoolResultsImageURL}
                alt={school.name}
                style={{ 
                  width: isMobile ? 80 : 128, 
                  height: isMobile ? 80 : 128, 
                  objectFit: 'contain', 
                  borderRadius: 12, 
                  marginRight: isMobile ? 0 : 32, 
                  background: '#fff', 
                  flexShrink: 0 
                }}
                onError={e => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'https://via.placeholder.com/128x128/1976d2/ffffff?text=School';
                }}
              />
            ) : null}
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1, height: 128, alignItems: 'flex-start', minWidth: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', minWidth: 0, gap: 1 }}>
                  {/* Program Name and Star Container */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                                         <Typography 
                       variant={isMobile ? 'h6' : 'h5'}
                       sx={{ 
                         textAlign: 'left', 
                         mb: 0, 
                         wordWrap: 'break-word',
                         overflowWrap: 'break-word',
                         hyphens: 'auto',
                         minWidth: 0,
                         flex: 1,
                         fontSize: isMobile ? '1.25rem' : undefined
                       }}
                     >
                      {program.program}{program.programCode ? ` (${program.programCode})` : ''}
                    </Typography>
                    <Tooltip title={isFavorite ? `Remove from ${getFavouritesLabel().toLowerCase()}` : `Add to ${getFavouritesLabel().toLowerCase()}`}>
                      <IconButton 
                        onClick={toggleFavorite}
                        disabled={favoriteLoading}
                        sx={{ 
                          color: isFavorite ? '#8dd439' : 'grey.400',
                          '&:hover': {
                            color: isFavorite ? '#7bc536' : 'grey.600',
                          },
                          flexShrink: 0
                        }}
                      >
                        {favoriteLoading ? (
                          <CircularProgress size={36} />
                        ) : isFavorite ? (
                          <Star sx={{ fontSize: isMobile ? '28px' : '36px' }} />
                        ) : (
                          <StarBorder sx={{ fontSize: isMobile ? '28px' : '36px' }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                  
                  {/* Eligibility Status Container */}
                  <Tooltip title={
                        (() => {
                              const eligibility = getCachedEligibility();
                              if (!eligibility || localEligibilityLoading) {
                                return 'Eligibility not checked';
                              }
                              // Use the same tooltip logic as SchoolProfile - use alertMessage from eligibility data
                              return (
                                <div>
                                  <div>{eligibility.alertMessage || 'Eligibility checked'}</div>
                                  {eligibility.nextYearSuggestion && eligibility.nextYearSuggestion !== "" && (
                                    <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#f44336' }}>
                                      💡 {eligibility.nextYearSuggestion}
                                    </div>
                                  )}
                                </div>
                              );
                            })()
                      }
                      componentsProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: '#f5f5f5',
                            color: '#333',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            fontSize: '14px',
                            padding: '12px',
                            maxWidth: '300px'
                          }
                        }
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          {(() => {
                            const eligibility = getCachedEligibility();
                            if (!eligibility || localEligibilityLoading) {
                              return (
                                <Box sx={{
                                  width: 54,
                                  height: 54,
                                  borderRadius: '50%',
                                  backgroundColor: 'grey.400',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  animation: (localEligibilityLoading || eligibilityLoading) ? 'pulse 1.5s ease-in-out infinite' : 'none'
                                }}>
                                  <HelpIcon sx={{ color: 'white', fontSize: 36 }} />
                                </Box>
                              );
                            }
                              
                              const userGrade = getUserGrade();
                              const isGrade10 = userGrade === '10';
                              
                              if (eligibility.ifEligible === true) {
                                if (isGrade10) {
                                  // Grade 10: Fosterway green circle with white full vertical arrow for on track
                                  return (
                                    <Box sx={{
                                      width: 54,
                                      height: 54,
                                      borderRadius: '50%',
                                      backgroundColor: '#8dd439', // Fosterway green (lighter shade)
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      <ArrowUpwardIcon sx={{ color: 'white', fontSize: 36 }} />
                                    </Box>
                                  );
                                } else {
                                  // Other grades: Original green check
                                  return <CheckCircleIcon sx={{ color: 'success.main', fontSize: '60px' }} />;
                                }
                              } else if (eligibility.ifEligible === false) {
                                if (isGrade10) {
                                // Grade 10: Check if it's a grade issue vs missing courses
                                  const hasGradeIssues = eligibility.gradeIssues && eligibility.gradeIssues.length > 0;
                                  const hasMissingCourses = eligibility.missingCourses && eligibility.missingCourses.length > 0;
                                  
                                  if (hasGradeIssues) {
                                    // Grade 10: Yellow circle with white full vertical arrow for grades need work (prioritize over missing courses)
                                    return (
                                      <Box sx={{
                                        width: 54,
                                        height: 54,
                                        borderRadius: '50%',
                                        backgroundColor: '#ffc107', // Yellow
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}>
                                        <ArrowUpwardIcon sx={{ color: 'white', fontSize: 36 }} />
                                      </Box>
                                    );
                                  } else if (hasMissingCourses) {
                                    // Grade 10: Blue circle with white undo arrow for missing prerequisites (only if no grade issues)
                                    return (
                                      <Box sx={{
                                        width: 54,
                                        height: 54,
                                        borderRadius: '50%',
                                        backgroundColor: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}>
                                        <UndoIcon sx={{ color: 'white', fontSize: 36 }} />
                                      </Box>
                                    );
                                  } else {
                                    // Grade 10: Fallback - should not happen but just in case
                                    return (
                                      <Box sx={{
                                        width: 54,
                                        height: 54,
                                        borderRadius: '50%',
                                        backgroundColor: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}>
                                        <UndoIcon sx={{ color: 'white', fontSize: 36 }} />
                                      </Box>
                                    );
                                  }
                                } else {
                                  // Other grades: Check if this is a Grade 12 student with low average
                                  if (userGrade === '12' && 
                                      eligibility.calculatedAverage && 
                                      eligibility.programAverage && 
                                      eligibility.calculatedAverage < eligibility.programAverage &&
                                      (!eligibility.missingCourses || eligibility.missingCourses.length === 0) &&
                                      (!eligibility.gradeIssues || eligibility.gradeIssues.length === 0)) {
                                    // Grade 12: Blue circle with split arrow for low average (has courses but average too low)
                                    return (
                                      <Box sx={{
                                        width: 54,
                                        height: 54,
                                        borderRadius: '50%',
                                        backgroundColor: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}>
                                        <AltRouteIcon sx={{ color: 'white', fontSize: 36 }} />
                                      </Box>
                                    );
                                  } else {
                                    // Other grades: Original blue circle with split arrow
                                    return (
                                      <Box sx={{
                                        width: 54,
                                        height: 54,
                                        borderRadius: '50%',
                                        backgroundColor: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}>
                                        <AltRouteIcon sx={{ color: 'white', fontSize: 36 }} />
                                      </Box>
                                    );
                                  }
                                }
                              } else {
                                // isEligible is null - check if there are missing courses or grade issues
                                const hasGradeIssues = eligibility.gradeIssues && eligibility.gradeIssues.length > 0;
                                const hasMissingCourses = eligibility.missingCourses && eligibility.missingCourses.length > 0;
                                
                                // Check if this is a Grade 12 student with low average (even when isEligible is null)
                                if (userGrade === '12' && 
                                    eligibility.calculatedAverage && 
                                    eligibility.programAverage && 
                                    eligibility.calculatedAverage < eligibility.programAverage &&
                                    (!hasMissingCourses) &&
                                    (!hasGradeIssues)) {
                                  // Grade 12: Blue circle with split arrow for low average (has courses but average too low)
                                  return (
                                    <Box sx={{
                                      width: 54,
                                      height: 54,
                                      borderRadius: '50%',
                                      backgroundColor: 'primary.main',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      <AltRouteIcon sx={{ color: 'white', fontSize: 36 }} />
                                    </Box>
                                  );
                                } else if (hasGradeIssues || hasMissingCourses) {
                                  // Show blue split arrow for missing courses or grade issues
                                  return (
                                    <Box sx={{
                                      width: 54,
                                      height: 54,
                                      borderRadius: '50%',
                                      backgroundColor: 'primary.main',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      <AltRouteIcon sx={{ color: 'white', fontSize: 36 }} />
                                    </Box>
                                  );
                                } else {
                                  // No issues found - show green checklist
                                  return (
                                    <Box sx={{
                                      width: 54,
                                      height: 54,
                                      borderRadius: '50%',
                                      backgroundColor: '#8dd439',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      <AssignmentIcon sx={{ color: 'white', fontSize: 36 }} />
                                    </Box>
                                  );
                                }
                              }
                            })()}
                        </Box>
                      </Tooltip>
                    </Box>
                    <Typography 
                      variant="subtitle2" 
                      color="text.secondary" 
                      sx={{
                        textAlign: 'left',
                        mt: -0.5
                      }}
                    >
                      {school ? `${school.city}, ${school.province}` : ''}
                    </Typography>
              </Box>
                  </Box>
                </Box>
                
                {program.url && (
                  <Box sx={{ display: 'flex', gap: 3, mt: 1, flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center' }}>
                    {/* Chips Section */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {program.category && (
                        <Chip
                          label={`Program Category: ${program.category}`}
                          color="default"
                          sx={{ fontWeight: 500 }}
                        />
                      )}
                      <Chip
                        label="Program Website"
                        clickable
                        color="primary"
                        onClick={handleProgramWebsiteClick}
                        sx={{ fontWeight: 500 }}
                      />
                    </Box>
                    
                    {/* Eligibility Indicator Descriptions Link */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Link
                        component="button"
                        onClick={() => setEligibilityIndicatorsModalOpen(true)}
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          font: 'inherit',
                          fontSize: '0.875rem',
                          '&:hover': {
                            color: 'primary.dark',
                          },
                        }}
                      >
                        Eligibility Indicator Descriptions
                      </Link>
                    </Box>
                  </Box>
                )}
          </Paper>

        {/* Program Description Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Program Description</Typography>
          <Typography variant="body1" color="text.secondary">
            {program.description}
          </Typography>
        </Paper>

        {/* Program Prerequisites and Grade 12 Courses Section */}
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left Column: Prerequisites and Eligibility Notes */}
          <Box sx={{ flex: '0 0 50%', display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Program Prerequisites Section */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Program Prerequisites</Typography>
              <Typography variant="body1" color="text.secondary">
                {program.prerequisites ? program.prerequisites.replace(/\(50%\)/g, '') : ''}
              </Typography>
            </Paper>

            {/* Additional Requirements Section */}
            {program.additionalRequirements && program.additionalRequirements.trim() !== '' && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Additional Requirements</Typography>
                <Typography variant="body1" color="text.secondary">
                  {program.additionalRequirements}
                </Typography>
              </Paper>
            )}

            {/* Eligibility Notes Section */}
            <Paper sx={{ p: 3, border: `2px solid ${getEligibilityBorderColor()}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Eligibility Notes</Typography>
                {renderEligibilityIcon(38)}
              </Box>
              {(() => {
                const eligibility = getCachedEligibility();
                if (!eligibility) return null;
                
                return (
                  <>
                    <Typography 
                      variant="body1" 
                      color={
                        eligibility.ifEligible === true 
                          ? 'text.primary' 
                          : eligibility.ifEligible === false 
                            ? 'primary.main' 
                            : 'text.primary'
                      }
                      sx={{ mb: 1 }}
                    >
                      {eligibility.alertMessage || 'Eligibility checked'}
                    </Typography>
                    
                    {/* Next Year Suggestion */}
                    {eligibility.nextYearSuggestion && eligibility.nextYearSuggestion !== "" && (
                    <Typography 
                      variant="body2" 
                      color="error.main" 
                      sx={{ fontStyle: 'italic' }}
                    >
                      <Box 
                        component="span" 
                        sx={{ 
                          display: 'inline-block',
                          fontSize: '1.2em',
                          filter: 'drop-shadow(0 0 0 black) drop-shadow(0 0 0 black) drop-shadow(0 0 0 black) drop-shadow(0 0 0 black)',
                          marginRight: '4px'
                        }}
                      >
                        💡
                      </Box>
                      {eligibility.nextYearSuggestion}
                    </Typography>
                  )}
                  </>
                );
              })()}
            </Paper>
          </Box>
          
          {/* Calculated Courses Section - 50% width */}
          <Paper sx={{ p: 3, flex: '0 0 50%', border: `2px solid ${getEligibilityBorderColor()}` }}>
            <Typography variant="h6" gutterBottom>
              Calculated Courses {displayCourses.gradeLevel && `(Grade ${displayCourses.gradeLevel})`}
            </Typography>
            {displayCourses.courses.length > 0 ? (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Course</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Grade</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayCourses.courses.map((course, index) => {
                        const eligibility = getCachedEligibility();
                        const isUsedInCalculation = eligibility?.coursesUsedInAverage?.includes(course.courseCode);
                        const hasGradeIssue = eligibility?.coursesWithGradeIssues?.includes(course.courseCode);
                        
                        
                        // Determine styling based on course status
                        let textColor = 'inherit';
                        let fontWeight = 'normal';
                        let fontStyle = 'normal'; // Default font style
                        
                        // Check if the course is a prerequisite for this program
                        const isExactMatch = prerequisiteCourses.exactMatches.has(course.courseCode);
                        const isTopPatternMatch = topPrerequisiteCourses.has(course.courseCode);
                        const isPrerequisite = isExactMatch || isTopPatternMatch;
                        
                        if (isPrerequisite) {
                          fontStyle = 'italic';
                        }
                        
                        if (isUsedInCalculation) {
                          fontWeight = 'bold'; // Always bold if used in calculation
                          if (hasGradeIssue) {
                            textColor = 'error.main'; // Red if used in calculation but has grade issue
                          } else {
                            textColor = 'inherit'; // Black if used in calculation and no grade issue
                          }
                        } else if (hasGradeIssue) {
                          textColor = 'error.main'; // Red for grade issues (not used in calculation)
                          fontWeight = 'normal';
                        }
                        
                        return (
                          <TableRow key={index}>
                            <TableCell sx={{ 
                              fontWeight: fontWeight,
                              fontStyle: fontStyle,
                              color: textColor
                            }}>
                              {course.courseCode}
                            </TableCell>
                            <TableCell sx={{ 
                              fontWeight: fontWeight,
                              fontStyle: fontStyle,
                              color: textColor
                            }}>
                              {course.grade}%
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Add footnote when Grade 11 courses are used in average calculation */}
                {(() => {
                  const eligibility = getCachedEligibility();
                  const coursesUsedInAverage = eligibility?.coursesUsedInAverage || [];
                  const grade12CoursesUsed = displayCourses.courses.filter(course => 
                    coursesUsedInAverage.includes(course.courseCode)
                  );
                  
                  // If more courses are used in average than Grade 12 courses shown in bold,
                  // it means Grade 11 courses were included
                  if (coursesUsedInAverage.length > grade12CoursesUsed.length) {
                    return (
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          fontStyle: 'italic', 
                          mt: 2, 
                          fontSize: '0.875rem' 
                        }}
                      >
                        Grade 11 courses may have been used to calculate your overall average for this program.
                      </Typography>
                    );
                  }
                  return null;
                })()}

              </>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No courses or grades found. Please{' '}
                <Typography
                  component="span"
                  variant="body1"
                  color="primary"
                  sx={{ 
                    cursor: 'pointer', 
                    textDecoration: 'underline',
                    fontWeight: 'inherit'
                  }}
                  onClick={() => navigate('/my-student-profile?tab=academics')}
                >
                  update your profile with your courses and grades
                </Typography>
                .
              </Typography>
            )}
          </Paper>
        </Box>

        {/* Debug Information Section - Only visible when debug mode is enabled */}
        {showPrerequisitesDebug && (
          <Paper sx={{ p: 3, mt: 3, backgroundColor: '#f5f5f5', border: '2px solid #1976d2' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
              🔍 Prerequisites Debug Information
            </Typography>
            
            {/* Raw Prerequisites Data */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Raw Prerequisites Data:
              </Typography>
              <Box sx={{ 
                backgroundColor: '#fff', 
                p: 2, 
                borderRadius: 1, 
                border: '1px solid #ddd',
                maxHeight: '400px',
                overflow: 'auto'
              }}>
                {(() => {
                  const prerequisiteData = getPrerequisitesForProgram(program.id);
                  
                  // Find the unique Firebase ID for this prerequisite
                  let prerequisiteId = null;
                  if (prerequisiteData) {
                    for (const [id, prereq] of Object.entries(prerequisites)) {
                      if (prereq.ProgramID === program.id) {
                        prerequisiteId = id;
                        break;
                      }
                    }
                  }
                  
                  // Debug: Show all prerequisite records that might match
                  const potentialMatches = Object.entries(prerequisites)
                    .filter(([id, prereq]) => 
                      prereq.ProgramID === program.id || 
                      (prereq.ProgramName && prereq.ProgramName.includes(program.program || ''))
                    );
                  
                  // Check if this program is being processed by eligibility calculation
                  const isInAllPrograms = programs.find(p => p.id === program.id);
                  const programSchool = isInAllPrograms?.schoolName || 'Unknown';
                  
                  return (
                    <div>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                        Prerequisite ID: {prerequisiteId || 'Not Found'}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#d32f2f' }}>
                        Program ID being searched: {program.id}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                        Potential matches: {potentialMatches.length}
                      </Typography>
                      
                      
                      {potentialMatches.length > 0 && (
                        <div style={{ marginBottom: '10px' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#2e7d32' }}>
                            Potential Matches:
                          </Typography>
                          {potentialMatches.map(([id, prereq]) => (
                            <div key={id} style={{ marginLeft: '10px', fontSize: '11px' }}>
                              <strong>ID:</strong> {id}<br/>
                              <strong>ProgramID:</strong> {prereq.ProgramID}<br/>
                              <strong>ProgramName:</strong> {prereq.ProgramName}<br/>
                              <hr style={{ margin: '5px 0' }}/>
                            </div>
                          ))}
                        </div>
                      )}
                      <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(prerequisiteData, null, 2)}
                      </pre>
                    </div>
                  );
                })()}
              </Box>
            </Box>

            {/* Processed Prerequisites Analysis */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Processed Prerequisites Analysis:
              </Typography>
              <Box sx={{ 
                backgroundColor: '#fff', 
                p: 2, 
                borderRadius: 1, 
                border: '1px solid #ddd'
              }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Exact Course Matches:</strong> {Array.from(prerequisiteCourses.exactMatches).join(', ') || 'None'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Pattern Requirements:</strong> 
                  {prerequisiteCourses.patterns.has4M ? ' 4M' : ''}
                  {prerequisiteCourses.patterns.has4U ? ' 4U' : ''}
                  {!prerequisiteCourses.patterns.has4M && !prerequisiteCourses.patterns.has4U ? ' None' : ''}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Required Count:</strong> {prerequisiteCourses.patterns.requiredCount}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Top Pattern Matches:</strong> {Array.from(topPrerequisiteCourses).join(', ') || 'None'}
                </Typography>
              </Box>
            </Box>

            {/* User Courses Analysis */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                User Courses Analysis:
              </Typography>
              <Box sx={{ 
                backgroundColor: '#fff', 
                p: 2, 
                borderRadius: 1, 
                border: '1px solid #ddd'
              }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Display Grade Level:</strong> {displayCourses.gradeLevel || 'None'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Total Courses:</strong> {displayCourses.courses.length}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>User Courses:</strong> {JSON.stringify(user?.courses || {}, null, 2)}
                </Typography>
              </Box>
            </Box>

            {/* Eligibility Calculation Details */}
            {(() => {
              const eligibility = getCachedEligibility();
              if (!eligibility) return null;
              
              return (
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Eligibility Calculation Details:
                  </Typography>
                  <Box sx={{ 
                    backgroundColor: '#fff', 
                    p: 2, 
                    borderRadius: 1, 
                    border: '1px solid #ddd',
                    maxHeight: '300px',
                    overflow: 'auto'
                  }}>
                    <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(eligibility, null, 2)}
                    </pre>
                  </Box>
                </Box>
              );
            })()}
          </Paper>
        )}
      </Box>
      {/* Sidebar: Program Details */}
      <Box sx={{ flex: 1, minWidth: 280 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Program Details</Typography>
          <Box>
            {[
              { label: 'School', value: program.schoolName, icon: <SchoolIcon fontSize="small" />, isClickable: true },
              { label: 'Estimated Minimum', value: program.estimatedMinimum ? (program.estimatedMinimum === '50' ? '-' : `${program.estimatedMinimum}%`) : 'N/A', icon: <AssessmentIcon fontSize="small" />, isClickable: false },
              { label: 'Experiential Learning', value: program.experientialLearning, icon: <WorkIcon fontSize="small" />, isClickable: false },
              { label: 'Language', value: program.language, icon: <LanguageIcon fontSize="small" />, isClickable: false },
              { label: 'Credential', value: program.degree, icon: <BookIcon fontSize="small" />, isClickable: false },
            ].map((item) => (
              <Box key={item.label} sx={{ py: 1, borderBottom: '1px solid #eee', '&:last-child': { borderBottom: 'none' } }}>
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {item.icon}
                    <Typography variant="body2">{item.label}</Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      pl: 5,
                      ...(item.isClickable && {
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: 'primary.dark'
                        }
                      })
                    }}
                    onClick={item.isClickable ? handleSchoolClick : undefined}
                  >
                    {item.value}
                  </Typography>
                </>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Students Who Favourited This Program (Guidance users only) */}
        {user?.isGuidanceUser && program && (
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              {user?.schoolName} students who {user?.country === 'US' ? 'favorited' : 'favourited'} {program.program}
            </Typography>
            {guidanceStudentsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : studentsWhoFavoritedProgram.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No students have favorited this program at this school.
              </Typography>
            ) : (
              <List>
                {studentsWhoFavoritedProgram.map((student) => (
                  <ListItem
                    key={student.uid}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      px: 0,
                      py: 1,
                    }}
                    onClick={() => {
                      const fromGuidance = searchParams.get('fromGuidance');
                      navigate(`/students/${student.uid}`, { 
                        state: { 
                          fromProgram: true, 
                          programId: program.id,
                          fromGuidance: fromGuidance === '1' 
                        } 
                      });
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={student.profileImage || user?.userIcon || undefined}
                        alt={`${student.firstName} ${student.lastName}`}
                        sx={{ 
                          width: 40, 
                          height: 40,
                          ...(!student.profileImage && {
                            '& .MuiAvatar-img': {
                              objectFit: 'contain',
                            },
                          }),
                        }}
                      >
                        {!student.profileImage && !user?.userIcon && (student.firstName?.[0] || student.lastName?.[0] || '?')}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${student.lastName}, ${student.firstName}`}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: { fontWeight: 500 },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        )}
      </Box>

      {/* Custom Eligibility Indicators Modal */}
      <CustomEligibilityIndicatorsModal
        open={eligibilityIndicatorsModalOpen}
        onClose={() => setEligibilityIndicatorsModalOpen(false)}
      />
    </Box>
  );
};

export default ProgramProfile; 
